import { useEffect, useState } from 'react';

/**
 * Which of a page's anchored sections the reader is currently in.
 *
 * A single scroll listener, read once per frame, rather than one
 * `IntersectionObserver` per section: the question is not "is this section
 * visible" — several always are — but "which one has the reader reached", and
 * that is the last heading to have passed under the sticky header.
 *
 * It returns `undefined` until the first frame after mount, which is what the
 * prerendered HTML wants: no link is marked current in markup that was built
 * before anybody scrolled, so the static page never ships a wrong highlight.
 */
export function useScrollSpy(ids: readonly string[], offset = 140) {
  const [activeId, setActiveId] = useState<string>();
  // The ids are a fresh array each render; their identity is not meaningful.
  const key = ids.join('|');

  useEffect(() => {
    const sectionIds = key ? key.split('|') : [];
    if (sectionIds.length === 0) return;

    let frame = 0;

    const measure = () => {
      frame = 0;
      // The foot of the page can never put the last section under the header,
      // so the last link would otherwise be unreachable.
      const atEnd = window.innerHeight + window.scrollY >= document.body.scrollHeight - 2;
      if (atEnd) {
        setActiveId(sectionIds[sectionIds.length - 1]);
        return;
      }

      const passed = sectionIds.filter((id) => {
        const top = document.getElementById(id)?.getBoundingClientRect().top;
        return top !== undefined && top <= offset;
      });
      setActiveId(passed[passed.length - 1] ?? sectionIds[0]);
    };

    const onScroll = () => {
      if (frame === 0) frame = window.requestAnimationFrame(measure);
    };

    measure();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      if (frame !== 0) window.cancelAnimationFrame(frame);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, [key, offset]);

  return activeId;
}
